import { api, get, post } from "./api";
import * as store from "./store";
import { compressImageWithSize, h, plural, toast, uuid } from "./util";
import { I } from "./icons";
import type { Garden } from "./types";
import type { Me } from "./auth";
import { isDemo } from "./demo";

export async function refreshGardens(): Promise<Garden[]> {
  if (isDemo()) return store.gardens();
  try {
    const r = await get<{ gardens: Garden[] }>("/api/gardens");
    await store.putGardens(r.gardens);
    return store.gardens();
  } catch {
    return store.gardens();
  }
}

export async function uploadGardenPhoto(garden: Garden, file: Blob): Promise<Garden> {
  const { blob, width, height } = await compressImageWithSize(file, 2400, 0.85);
  let updatedAt = new Date().toISOString();
  let finalW = width;
  let finalH = height;
  if (!isDemo()) {
    const fd = new FormData();
    fd.append("file", blob, "zahrada.jpg");
    const r = await api<{ photo_width: number; photo_height: number; updated_at?: string }>("POST", "/api/gardens/" + garden.id + "/photo", fd);
    finalW = r.photo_width || width;
    finalH = r.photo_height || height;
    if (r.updated_at) updatedAt = r.updated_at;
  }
  const updated: Garden = {
    ...garden,
    mode: "photo",
    photo_width: finalW,
    photo_height: finalH,
    photo_blob: blob,
    updated_at: updatedAt,
  };
  await store.putGarden(updated);
  return updated;
}

export async function patchGardenLink(
  garden: Garden,
  parent_id: string | null,
  parent_lat: number | null = null,
  parent_lng: number | null = null,
): Promise<Garden> {
  if (!isDemo()) {
    await api("PATCH", "/api/gardens/" + garden.id, { parent_id, parent_lat, parent_lng });
  }
  const updated: Garden = {
    ...garden,
    parent_id,
    parent_lat: parent_id ? parent_lat : null,
    parent_lng: parent_id ? parent_lng : null,
    updated_at: new Date().toISOString(),
  };
  await store.putGarden(updated);
  return updated;
}

export function promptRenameGarden(g: Garden, onRenamed: (updated: Garden) => void | Promise<void>) {
  const input = h("input", {
    type: "text",
    value: g.name,
    maxLength: 80,
    required: true,
    placeholder: "Název zahrady",
  }) as HTMLInputElement;
  const errEl = h("p", { class: "err", role: "alert", hidden: true });
  const cancelBtn = h("button", { type: "button", class: "btn ghost", onclick: () => ov.remove() }, "Zrušit");
  const saveBtn = h("button", { type: "submit", class: "btn primary" }, "Uložit");

  const form = h("form", { class: "card", style: "max-width:420px;width:100%" },
    h("h2", {}, "Přejmenovat zahradu"),
    h("label", { class: "field" }, h("span", {}, "Nový název"), input),
    errEl,
    h("div", { class: "btns even" }, cancelBtn, saveBtn));

  const ov = h("div", {
    class: "modal-backdrop",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Přejmenovat zahradu",
  }, form);

  ov.addEventListener("click", (e) => {
    if (e.target === ov) ov.remove();
  });

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    const newName = input.value.trim();
    if (!newName) { input.focus(); return; }
    errEl.hidden = true;
    saveBtn.setAttribute("disabled", "");
    cancelBtn.setAttribute("disabled", "");
    try {
      if (!isDemo()) {
        await api("PATCH", "/api/gardens/" + g.id, { name: newName });
      }
      const updated: Garden = { ...g, name: newName, updated_at: new Date().toISOString() };
      await store.putGarden(updated);
      ov.remove();
      toast("Zahrada přejmenována");
      await onRenamed(updated);
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Přejmenování se nezdařilo.";
      errEl.hidden = false;
      saveBtn.removeAttribute("disabled");
      cancelBtn.removeAttribute("disabled");
    }
  });

  document.body.append(ov);
  input.focus();
  input.select();
}

export function confirmDeleteGarden(g: Garden, plantCount: number, onDeleted: () => void | Promise<void>) {
  const isOwner = !g.role || g.role === "owner";
  const errEl = h("p", { class: "err", role: "alert", hidden: true });
  const cancelBtn = h("button", { type: "button", class: "btn ghost", onclick: () => ov.remove() }, "Zrušit");
  const confirmBtn = h("button", {
    type: "button",
    class: "btn danger",
    html: I.trash + "<span>" + (isOwner ? "Smazat zahradu" : "Odebrat zahradu") + "</span>",
  });

  const desc = isOwner
    ? "Opravdu chceš trvale smazat zahradu „" + g.name + "“" +
      (plantCount ? " (obsahuje " + plantCount + " " + plural(plantCount, "rostlinu", "rostliny", "rostlin") + ")" : "") +
      "? Tuto akci nelze vrátit zpět."
    : "Opravdu chceš odebrat sdílenou zahradu „" + g.name + "“ ze svého seznamu?";

  const card = h("div", { class: "card", style: "max-width:420px;width:100%" },
    h("h2", {}, isOwner ? "Smazat zahradu?" : "Odebrat sdílenou zahradu?"),
    h("p", { class: "hint", style: "font-size:15.5px;color:var(--fg)" }, desc),
    errEl,
    h("div", { class: "btns even" }, cancelBtn, confirmBtn));

  const ov = h("div", {
    class: "modal-backdrop",
    role: "alertdialog",
    "aria-modal": "true",
    "aria-label": isOwner ? "Potvrzení smazání zahrady" : "Potvrzení odebrání zahrady",
  }, card);

  ov.addEventListener("click", (e) => {
    if (e.target === ov) ov.remove();
  });

  confirmBtn.onclick = async () => {
    errEl.hidden = true;
    confirmBtn.setAttribute("disabled", "");
    cancelBtn.setAttribute("disabled", "");
    try {
      if (!isDemo()) {
        await api("DELETE", "/api/gardens/" + g.id);
      }
      await store.deleteGarden(g.id);
      // Unlink any local subgardens that pointed to this deleted garden
      for (const sub of await store.gardens()) {
        if (sub.parent_id === g.id) {
          await store.putGarden({ ...sub, parent_id: null, parent_lat: null, parent_lng: null });
        }
      }
      try {
        if (localStorage.getItem("zahrada:garden") === g.id) localStorage.removeItem("zahrada:garden");
      } catch {}
      ov.remove();
      toast(isOwner ? "Zahrada „" + g.name + "“ byla smazána" : "Sdílená zahrada byla odebrána");
      await onDeleted();
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Zahradu se nepodařilo odebrat.";
      errEl.hidden = false;
      confirmBtn.removeAttribute("disabled");
      cancelBtn.removeAttribute("disabled");
    }
  };

  document.body.append(ov);
  cancelBtn.focus();
}

export async function renderGardens(root: HTMLElement, me: Me, nav: { open: (g: Garden, editParcels?: boolean) => void; logout: () => void }) {
  const list = h("ul", { class: "glist" });
  let formContainer = h("div", {});
  const page = h("div", { class: "page" }, h("div", { class: "page-inner" },
    h("div", { class: "row-between" },
      h("div", { class: "logo", html: I.tree + "<b>Zahrada</b>" }),
      h("button", { class: "btn ghost", onclick: nav.logout, title: me.email, html: I.logout + "<span>" + (isDemo() ? "Ukončit ukázku" : "Odhlásit") + "</span>" })),
    h("h1", {}, "Moje zahrady"),
    list,
    formContainer,
    h("p", { class: "hint" }, isDemo() ? "Ukázka bez přihlášení: nic se neukládá." : "Přihlášen jako " + me.email)));
  root.replaceChildren(page);

  const draw = async (gs: Garden[]) => {
    list.replaceChildren();
    const byId = new Map(gs.map((x) => [x.id, x]));
    if (!gs.length) list.append(h("li", { class: "empty" }, "Zatím nemáš žádnou zahradu. Založ první níže."));
    for (const g of gs) {
      const plants = (await store.plantsOf(g.id)).length;
      const parc = g.parcels.map((p) => p.cislo).join(", ");
      const parentGarden = g.parent_id ? byId.get(g.parent_id) : undefined;
      const modeLabel = g.mode === "photo"
        ? "podle fotky" + (parentGarden ? " · v zahradě " + parentGarden.name : " · samostatný")
        : (g.parcels[0] ? g.parcels[0].ku_nazev + " · " + parc : "bez parcel");
      const shareInfo = g.role === "read"
        ? " · sdíleno (jen čtení" + (g.owner_email ? " od " + g.owner_email : "") + ")"
        : g.role === "write"
          ? " · sdíleno (úpravy" + (g.owner_email ? " od " + g.owner_email : "") + ")"
          : "";
      const isOwner = !g.role || g.role === "owner";
      const delBtn = h("button", {
        type: "button",
        class: "gdel",
        title: isOwner ? "Smazat zahradu" : "Odebrat sdílenou zahradu",
        "aria-label": (isOwner ? "Smazat zahradu " : "Odebrat sdílenou zahradu ") + g.name,
        onclick: (e: MouseEvent) => {
          e.stopPropagation();
          confirmDeleteGarden(g, plants, async () => {
            await draw(await store.gardens());
          });
        },
        html: I.trash,
      });
      list.append(h("li", { class: "gitem" },
        h("button", { class: "gopen", onclick: () => nav.open(g) },
          h("span", { class: "gi" }, g.name.slice(0, 1).toUpperCase()),
          h("span", { class: "gt" },
            h("b", {}, g.name),
            h("small", {}, modeLabel + (plants ? " · " + plants + " " + plural(plants, "rostlina", "rostliny", "rostlin") : "") + shareInfo))),
        delBtn));
    }
    formContainer.replaceChildren(newGardenForm(nav.open, gs));
  };
  await draw(await store.gardens());
  await draw(await refreshGardens());
}

function newGardenForm(open: (g: Garden, editParcels?: boolean) => void, existingGardens: Garden[]) {
  let mode: "map" | "photo" = "map";
  let selectedFile: File | null = null;
  let previewUrl: string | null = null;

  const mapGardens = existingGardens.filter((g) => g.mode !== "photo" && g.role !== "read");
  const name = h("input", { id: "newGardenName", placeholder: "např. Chata u lesa nebo Záhonek u terasy", maxLength: 80, required: true }) as HTMLInputElement;
  const modeTabs = h("div", { class: "tabs", role: "tablist", "aria-label": "Typ zahrady" });
  const modeHint = h("p", { class: "hint" });
  const photoBox = h("div", { class: "field", hidden: true });
  const parentSelect = h("select", { id: "newGardenParent" },
    h("option", { value: "" }, "Samostatný záhonek / zahrada (bez propojení)"),
    ...mapGardens.map((mg) => h("option", { value: mg.id }, "Propojit se zahradou: " + mg.name)),
  ) as HTMLSelectElement;
  const parentField = h("label", { class: "field", hidden: true },
    h("span", {}, "Propojení s hlavní zahradou"),
    parentSelect);
  const fileCam = h("input", { type: "file", accept: "image/*", capture: "environment", hidden: true }) as HTMLInputElement;
  const fileGal = h("input", { type: "file", accept: "image/*", hidden: true }) as HTMLInputElement;
  const previewWrap = h("div", { hidden: true });
  const errEl = h("p", { class: "err", role: "alert", hidden: true });
  const btn = h("button", { class: "btn primary", type: "submit", html: I.plus + "<span>Založit zahradu</span>" });

  const updatePreview = () => {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
    if (!selectedFile) {
      previewWrap.hidden = true;
      previewWrap.replaceChildren();
      return;
    }
    previewUrl = URL.createObjectURL(selectedFile);
    previewWrap.hidden = false;
    previewWrap.replaceChildren(
      h("div", { class: "photo-preview" },
        h("img", { src: previewUrl, alt: "Náhled fotky zahrady" }),
        h("button", {
          type: "button",
          class: "btn ghost",
          onclick: () => { selectedFile = null; updatePreview(); },
          html: I.trash + "<span>Odebrat fotku</span>",
        })));
  };

  const onFilePick = (ev: Event) => {
    const input = ev.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = "";
    if (!f) return;
    selectedFile = f;
    errEl.hidden = true;
    updatePreview();
  };
  fileCam.onchange = onFilePick;
  fileGal.onchange = onFilePick;

  photoBox.append(
    h("span", {}, "Fotka záhonku nebo části zahrady"),
    previewWrap,
    h("div", { class: "btns even" },
      h("button", { type: "button", class: "btn ghost", onclick: () => fileCam.click(), html: I.camera + "<span>Vyfotit</span>" }),
      h("button", { type: "button", class: "btn ghost", onclick: () => fileGal.click(), html: I.image + "<span>Vybrat fotku</span>" })),
    fileCam,
    fileGal,
  );

  const drawMode = () => {
    const opts: ["map" | "photo", string][] = [
      ["map", "Podle parcel (mapa)"],
      ["photo", "Podle fotky (záhonek)"],
    ];
    modeTabs.replaceChildren(...opts.map(([k, label]) =>
      h("button", {
        type: "button",
        role: "tab",
        "aria-selected": String(mode === k),
        onclick: () => { mode = k; errEl.hidden = true; drawMode(); },
      }, label)));
    if (mode === "map") {
      photoBox.hidden = true;
      parentField.hidden = true;
      modeHint.textContent = "Parcely přidáš v dalším kroku: podle GPS, klepnutím do mapy nebo podle katastrálního území a čísla.";
    } else {
      photoBox.hidden = false;
      parentField.hidden = mapGardens.length === 0;
      modeHint.textContent = "Pro záhonek, skleník nebo část zahrady, kde je potřeba větší přesnost. Může fungovat samostatně nebo být propojený s hlavní zahradou.";
    }
  };
  drawMode();

  const form = h("form", { class: "card" },
    h("h2", {}, "Nová zahrada"),
    h("label", { class: "field" }, h("span", {}, "Název"), name),
    h("div", { class: "field" }, h("span", {}, "Podklad zahrady"), modeTabs),
    photoBox,
    parentField,
    modeHint,
    errEl,
    btn);

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    errEl.hidden = true;
    if (!name.value.trim()) { name.focus(); return; }
    if (mode === "photo" && !selectedFile) {
      errEl.textContent = "Vyfoť nebo vyber fotku, na které budeš označovat rostliny.";
      errEl.hidden = false;
      return;
    }
    const parentId = mode === "photo" && parentSelect.value ? parentSelect.value : null;
    btn.setAttribute("disabled", "");
    try {
      let garden: Garden = isDemo()
        ? { id: uuid(), name: name.value.trim(), mode, parent_id: parentId, parcels: [] }
        : (await post<{ garden: Garden }>("/api/gardens", { id: uuid(), name: name.value.trim(), mode, parent_id: parentId })).garden;
      if (mode === "photo" && selectedFile) {
        garden = await uploadGardenPhoto(garden, selectedFile);
      } else {
        await store.putGarden(garden);
      }
      open(garden, mode === "map");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Zahradu se nepodařilo založit.");
    } finally { btn.removeAttribute("disabled"); }
  });
  return form;
}
